import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Wallet, Check, X } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import toast from 'react-hot-toast';

interface Withdrawal {
  id: string;
  amount: string;
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  payout_reference: string | null;
  remarks: string | null;
  created_at: string;
  driver_profile?: { user?: { name?: string; phone?: string } };
  bank_account?: { bank_name?: string; account_number?: string; ifsc_code?: string };
}

const inr = (n: string | number) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  processing: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

export function PayoutsPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('all');

  const { data: withdrawals = [], isLoading } = useQuery({
    queryKey: ['withdrawals', status],
    queryFn: async (): Promise<Withdrawal[]> => {
      const res = await api.get(API_ENDPOINTS.PAYMENTS.WITHDRAWALS, {
        params: status !== 'all' ? { status } : {},
      });
      const raw = (res.data as any)?.data ?? res.data;
      return Array.isArray(raw) ? raw : [];
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.put(API_ENDPOINTS.PAYMENTS.APPROVE_WITHDRAWAL(id)),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['withdrawals'] }); toast.success('Withdrawal approved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Approve failed'),
  });
  const rejectMutation = useMutation({
    mutationFn: (id: string) => api.put(API_ENDPOINTS.PAYMENTS.REJECT_WITHDRAWAL(id), { remarks: 'Rejected by admin' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['withdrawals'] }); toast.success('Withdrawal rejected — wallet refunded'); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Reject failed'),
  });

  const pendingTotal = withdrawals.filter(w => w.status === 'pending' || w.status === 'processing')
    .reduce((s, w) => s + Number(w.amount || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Payouts" description="Driver wallet withdrawals (auto-paid via Cashfree; approve/reject the manual fallback)" />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Card className="flex-1 min-w-[220px]">
          <CardContent className="p-4 flex items-center gap-3">
            <Wallet className="h-5 w-5 text-amber-600" />
            <div>
              <div className="text-xs text-muted-foreground">In-progress payouts</div>
              <div className="text-lg font-bold">{inr(pendingTotal)}</div>
            </div>
          </CardContent>
        </Card>
        <Select
          value={status}
          onChange={(value) => setStatus(value)}
          className="w-48"
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'pending', label: 'Pending' },
            { value: 'processing', label: 'Processing' },
            { value: 'completed', label: 'Completed' },
            { value: 'rejected', label: 'Rejected' },
          ]}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : !withdrawals.length ? (
            <div className="text-center py-12 text-muted-foreground">No withdrawal requests.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Bank</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>UTR / Ref</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {withdrawals.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell className="whitespace-nowrap text-sm">{fmtDate(w.created_at)}</TableCell>
                    <TableCell>
                      <div className="font-medium">{w.driver_profile?.user?.name || 'Unknown'}</div>
                      <div className="text-xs text-muted-foreground">{w.driver_profile?.user?.phone || ''}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {w.bank_account?.bank_name || '—'}
                      {w.bank_account?.account_number ? <div className="text-xs text-muted-foreground">••••{w.bank_account.account_number.slice(-4)}</div> : null}
                    </TableCell>
                    <TableCell className="text-right font-semibold">{inr(w.amount)}</TableCell>
                    <TableCell><Badge className={STATUS_STYLES[w.status] || ''}>{w.status}</Badge></TableCell>
                    <TableCell className="text-xs font-mono">{w.payout_reference || '—'}</TableCell>
                    <TableCell className="text-right">
                      {w.status === 'pending' ? (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => approveMutation.mutate(w.id)} disabled={approveMutation.isPending}>
                            <Check className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => rejectMutation.mutate(w.id)} disabled={rejectMutation.isPending}>
                            <X className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">{w.status === 'completed' ? 'Paid' : w.status === 'processing' ? 'Auto-paying…' : '—'}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
